import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo } from 'react';

import useScreenSize from '../../../util/hooks/useScreenSize';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { bottomBarInset } from '../BottomNavigationBar/bottomBarInset';

const rootSx = (theme) => ({
  width: '100%',
  position: 'sticky',
  bottom: 0,
  zIndex: theme.tokens.zIndexTopbar,
  marginBottom: theme.tokens.BottomBar_height,
});

// The inset drops to 0 while the keyboard hides the bar; the token floor keeps the page from
// shrinking under the message composer then.
const clearRaisedActionSx = (theme) => ({
  ...rootSx(theme),
  marginBottom: `max(${bottomBarInset}, ${theme.tokens.BottomBar_height})`,
});

/**
 * The bottom nav hides on desktop width on the web, but stays in the app,
 * where the TopBar is off and tablets in landscape reach desktop width.
 * @param {object} params
 * @param {boolean} params.isDesktop
 * @param {boolean} params.inApp
 * @returns {boolean}
 */
export const shouldRenderBottombar = ({ isDesktop, inApp }) => !isDesktop || inApp;

/**
 * This is a wrapper component for different Layouts.
 * TopBar should be added to this wrapper.
 * @param {object} props
 * @param {string | null} [props.className] - Additional class to be added to the root element.
 * @param {string | null} [props.rootClassName] - Additional class to be added to the root element.
 * @param {boolean} [props.clearRaisedAction] - Reserve the room the bar measured for itself, raised
 *   centre action included, instead of the BottomBar_height token — for pages that end in a
 *   control. Pages sized to the viewport minus the token (the map pages) keep the default.
 * @param {React.JSX.Element | React.JSX.Element[] | null} props.children - Collection of children to be rendered.
 * @returns {React.JSX.Element | null}
 */

const LayoutWrapperBottombarComponent = ({
  className = null,
  rootClassName = null,
  clearRaisedAction = false,
  children,
}) => {
  const classes = classNames(rootClassName, className);

  const { isDesktop } = useScreenSize();
  if (!shouldRenderBottombar({ isDesktop, inApp: isCordova() })) return null;

  const sx = clearRaisedAction ? clearRaisedActionSx : rootSx;
  return (
    <Box className={classes || undefined} sx={rootClassName ? undefined : sx}>
      {children}
    </Box>
  );
};

LayoutWrapperBottombarComponent.displayName = 'LayoutWrapperBottombar';

const LayoutWrapperBottombar = memo(LayoutWrapperBottombarComponent);

export default LayoutWrapperBottombar;
